import React, { useState, useRef, useEffect } from 'react';
import { track } from '@vercel/analytics/react';
import { spriteSrc, fmtName, CARD_W, CARD_H, FULL_CARD_W, FULL_CARD_H, ART_WINDOW_LEFT, ART_WINDOW_TOP, CARD_FRAME_FULL_SRC } from '../spriteEngine';
import { buildCardPsdBlob, buildCardPngDataUrl } from '../psdExport';
import DraggableSprite from './DraggableSprite';
import './PokemonCard.css';

const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onloadend = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(blob);
});

const MINI_ZOOM = 0.46;

const makeOrderCode = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let out = '';
  for (let i = 0; i < 6; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
};

// Background art (public/backgrounds/*, watermarked — the order PNG/PSD export
// instead loads the clean public/Backgrounds full/* source directly, see
// bgExportUrl below) is pre-composited by the artist at the full 750x1050 card
// canvas, aligned to the template's die-cut window, so it's drawn onto a canvas
// sized/offset to crop out just that window for the live preview.
const BG_CANVAS_SCALE = 1;
const useBackgroundCanvas = (bgId) => {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      const w = CARD_W * BG_CANVAS_SCALE, h = CARD_H * BG_CANVAS_SCALE;
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, w, h);
      ctx.drawImage(
        img,
        0, 0, img.naturalWidth, img.naturalHeight,
        -ART_WINDOW_LEFT * BG_CANVAS_SCALE, -ART_WINDOW_TOP * BG_CANVAS_SCALE,
        FULL_CARD_W * BG_CANVAS_SCALE, FULL_CARD_H * BG_CANVAS_SCALE
      );
    };
    img.src = `/${bgId}`;
    return () => { cancelled = true; };
  }, [bgId]);
  return canvasRef;
};

const SpriteArtLayer = ({ bgObj, sprites, POKEMON_SPRITES, editable, selectedId, setSelectedId, onSpriteChange, onSpriteDelete, artLayerRef }) => {
  const bgCanvasRef = useBackgroundCanvas(bgObj.id);
  return (
  <div
    className="card-art-layer"
    onPointerDown={editable ? () => setSelectedId(null) : undefined}
  >
    {/* artLayerRef is queried by psdExport.js (via each sprite's data-sprite-id)
        to pull out the live, already-loaded <img> elements for rendering sprite
        layers into the order PNG/PSD canvases. */}
    <div className="card-art-inner" ref={artLayerRef}>
      <canvas className="card-bg-img" ref={bgCanvasRef} role="img" aria-label={bgObj.label} />
      {sprites.map(sprite => {
        const isPlaceholder = sprite.kind === 'pokemon' && !POKEMON_SPRITES.includes(sprite.name);
        return (
          <DraggableSprite
            key={sprite.id}
            sprite={sprite}
            src={spriteSrc(sprite.kind, sprite.name, POKEMON_SPRITES)}
            label={isPlaceholder ? fmtName(sprite.name) : null}
            selected={editable && selectedId === sprite.id}
            editable={editable}
            onSelect={setSelectedId}
            onChange={onSpriteChange}
            onDelete={onSpriteDelete}
          />
        );
      })}
    </div>
  </div>
  );
};

const PokemonCard = ({
  pokemon, bgObj,
  name, hp, bannerText, message,
  atk1Name, atk1Dmg, atk1Desc, atk2Name, atk2Dmg,
  sprites, POKEMON_SPRITES,
  selectedId, setSelectedId,
  onSpriteChange, onSpriteDelete,
  referralSource,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [copied, setCopied]       = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [pos, setPos]             = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadState, setUploadState] = useState('idle'); // idle | capturing | uploading | done | error
  const [designImage, setDesignImage] = useState(null); // { url, orderCode } | null
  const cardColumnRef = useRef(null);
  const artLayerRef    = useRef(null);
  const dragOrigin    = useRef(null);

  // Show mini overlay only on mobile when card scrolls out of view
  useEffect(() => {
    const el = cardColumnRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (window.innerWidth <= 820) {
          setShowOverlay(!entry.isIntersecting);
          if (entry.isIntersecting) setPos(null);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Pointer drag (mobile mini overlay repositioning — unrelated to sprite editing)
  const onPointerDown = (e) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    dragOrigin.current = {
      startX: e.clientX, startY: e.clientY,
      left: rect.left,   top: rect.top,
    };
    setIsDragging(true);
  };

  useEffect(() => {
    if (!isDragging) return;
    const overlayW = Math.round(350 * MINI_ZOOM);
    const overlayH = Math.round(500 * MINI_ZOOM);
    const onMove = (e) => {
      const dx = e.clientX - dragOrigin.current.startX;
      const dy = e.clientY - dragOrigin.current.startY;
      setPos({
        left: Math.max(8, Math.min(window.innerWidth  - overlayW - 8, dragOrigin.current.left + dx)),
        top:  Math.max(8, Math.min(window.innerHeight - overlayH - 8, dragOrigin.current.top  + dy)),
      });
    };
    const onUp = () => setIsDragging(false);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [isDragging]);

  const spriteLines = sprites.length
    ? sprites
        .slice()
        .sort((a, b) => a.z - b.z)
        .map((s, i) => {
          const label = s.kind === 'trainer' ? 'Trainer' : 'Pokémon';
          const nm = fmtName(s.name) || '(unnamed)';
          const details = s.kind === 'trainer' && s.details ? ` — ${s.details}` : '';
          return `${i + 1}. ${label}: ${nm}${details}`;
        }).join('\n')
    : '(no sprites placed)';

  const orderText = (orderCode) =>
`Design Code: ${orderCode || '(pending — see image filename)'}
Background: ${bgObj.label}

Placed sprites:
${spriteLines}

1. Card Name: ${name || 'Pokémon'}
2. HP: ${hp || '—'}
3. Gold Banner Message: ${bannerText || '(none)'}
4. Card Ability: ${atk1Name || '(none)'}
5. Ability Damage: ${atk1Dmg || '0'}
6. Ability Description: ${atk1Desc || '(none)'}
7. Box Message: ${message || '(none)'}`;

  const copyToClipboard = (orderCode) => {
    navigator.clipboard.writeText(orderText(orderCode)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }).catch(() => {
      // Clipboard permission denied/unavailable — the order text is still shown
      // in the modal for the customer to select and copy manually.
    });
  };

  // The generated PNG/PSD use the clean, non-watermarked background + card frame
  // (public/Backgrounds full/) — the live preview above keeps the watermarked
  // versions. Falls back to the watermarked background if a given background has
  // no clean counterpart yet (see BACKGROUNDS in App.js).
  const bgExportUrl = `/${bgObj.fullId || bgObj.id}`;

  // Best-effort layered PSD export, uploaded alongside the PNG for the designer's
  // use. Must never fail the customer-facing success/error outcome, so any
  // failure here is swallowed after logging — but captureAndUpload still awaits
  // it (see Promise.all below) so the modal's loading state covers it too.
  const uploadPsd = async (orderCode, background) => {
    try {
      const blob = await buildCardPsdBlob(artLayerRef.current, sprites, bgExportUrl, CARD_FRAME_FULL_SRC);
      const psdBase64 = await blobToDataUrl(blob);
      const res = await fetch('/api/upload-card-psd', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ psdBase64, orderCode, background }),
      });
      if (!res.ok) throw new Error('PSD upload failed');
    } catch (err) {
      console.error('PSD export/upload failed (non-blocking):', err);
    }
  };

  const captureAndUpload = async () => {
    setUploadState('capturing');
    setSelectedId(null);
    // let the deselect re-render (hides drag handles) before we snapshot the DOM
    await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));

    const orderCode = makeOrderCode();
    let dataUrl = null;
    try {
      dataUrl = await buildCardPngDataUrl(artLayerRef.current, sprites, bgExportUrl, CARD_FRAME_FULL_SRC);
      setUploadState('uploading');

      const pngUpload = fetch('/api/upload-card', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageBase64: dataUrl,
          orderCode,
          background: bgObj.label,
        }),
      }).then(async (res) => {
        if (!res.ok) throw new Error('Upload failed');
        const { url } = await res.json();
        return url;
      });

      // Wait for both uploads so the modal's loading spinner stays up until the
      // PSD is done too, not just the PNG.
      const [url] = await Promise.all([pngUpload, uploadPsd(orderCode, bgObj.label)]);
      setDesignImage({ url, orderCode, dataUrl });
      setUploadState('done');
      return orderCode;
    } catch (err) {
      console.error('Card snapshot upload failed:', err);
      setDesignImage({ url: null, orderCode, dataUrl });
      setUploadState('error');
      return orderCode;
    }
  };

  const handleOrder = async () => {
    track('order_card_clicked', { background: bgObj.label });
    setShowModal(true);
    document.body.style.overflow = 'hidden';
    const orderCode = await captureAndUpload();
    copyToClipboard(orderCode);
  };
  const handleClose = () => {
    setShowModal(false);
    setCopied(false);
    setShowGuide(false);
    setUploadState('idle');
    setDesignImage(null);
    document.body.style.overflow = '';
  };

  return (
    <div className="card-column" ref={cardColumnRef}>
      <div className="card-label">✨ Your Card Preview</div>
      <div className="card-preview-note">Preview only — we handcraft the final print based on your placement.</div>

      <div className={`pokemon-card${bgObj.label === 'Proposal' ? ' card-proposal' : ''}`}>
        <SpriteArtLayer
          bgObj={bgObj}
          sprites={sprites}
          POKEMON_SPRITES={POKEMON_SPRITES}
          editable={true}
          selectedId={selectedId}
          setSelectedId={setSelectedId}
          onSpriteChange={onSpriteChange}
          onSpriteDelete={onSpriteDelete}
          artLayerRef={artLayerRef}
        />
        <img className="card-template-overlay" src="/template.png" alt="" fetchPriority="high" />
        <div className="card-text-content">
          <div className="card-header">
            <div className="card-name">{name || 'Pokémon'}</div>
            <div className="card-hp-wrap">
              <span className="card-hp-label">HP</span>
              <span className="card-hp-val">{hp || '—'}</span>
            </div>
          </div>
          {bannerText && <div className="card-banner">{bannerText}</div>}
          <div className="card-attacks">
            <div className="card-attack">
              <div className="attack-row">
                <span className="attack-name">{atk1Name || 'Attack 1'}</span>
                <span className="attack-damage">{atk1Dmg || '0'}</span>
              </div>
              <div className="attack-desc">{atk1Desc}</div>
            </div>
          </div>
          {message && <div className="card-message">{message}</div>}
        </div>
      </div>

      <button className="cta-btn" onClick={handleOrder}>
        🛒 Order This Card
      </button>

      {/* Draggable mini overlay — mobile only, when card is scrolled out of view */}
      {showOverlay && (
        <div
          className={`card-mini-overlay${isDragging ? ' dragging' : ''}`}
          style={pos ? { left: pos.left, top: pos.top, bottom: 'auto', right: 'auto' } : {}}
          onPointerDown={onPointerDown}
        >
          <div className={`pokemon-card card-mini${bgObj.label === 'Proposal' ? ' card-proposal' : ''}`} style={{ zoom: MINI_ZOOM }}>
            <SpriteArtLayer
              bgObj={bgObj}
              sprites={sprites}
              POKEMON_SPRITES={POKEMON_SPRITES}
              editable={false}
            />
            <img className="card-template-overlay" src="/template.png" alt="" />
            <div className="card-text-content">
              <div className="card-header">
                <div className="card-name">{name || 'Pokémon'}</div>
                <div className="card-hp-wrap">
                  <span className="card-hp-label">HP</span>
                  <span className="card-hp-val">{hp || '—'}</span>
                </div>
              </div>
              {bannerText && <div className="card-banner">{bannerText}</div>}
              <div className="card-attacks">
                <div className="card-attack">
                  <div className="attack-row">
                    <span className="attack-name">{atk1Name || 'Attack 1'}</span>
                    <span className="attack-damage">{atk1Dmg || '0'}</span>
                  </div>
                  <div className="attack-desc">{atk1Desc}</div>
                </div>
              </div>
              {message && <div className="card-message">{message}</div>}
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="order-modal-overlay" onClick={handleClose}>
          <div className="order-modal" onClick={e => e.stopPropagation()}>
            <div className="order-modal-header">
              <h2>Your Order Request</h2>
              <button className="order-modal-close" onClick={handleClose}>✕</button>
            </div>

            {(uploadState === 'capturing' || uploadState === 'uploading') ? (
              <div className="order-modal-loading">
                <div className="order-spinner" aria-hidden="true" />
                <p className="order-modal-subtitle">
                  {uploadState === 'capturing' ? '📸 Capturing your design…' : '☁️ Saving your design for our designer…'}
                </p>
              </div>
            ) : (
              <>
                {uploadState === 'done' && (
                  <p className="order-modal-subtitle">
                    ✅ Design saved! Copied the order details below — paste them into the personalisation field on checkout.
                  </p>
                )}
                {uploadState === 'error' && (
                  <p className="order-modal-subtitle">
                    ⚠️ Couldn't auto-save your design. Please copy the order details below and message us on Etsy/Shopify so we can get your design.
                  </p>
                )}

                {designImage && (
                  <div className="order-code-badge">
                    Design Code: <strong>{designImage.orderCode}</strong>
                  </div>
                )}

                <pre className="order-modal-text">{orderText(designImage?.orderCode)}</pre>
                <button className="cta-btn" onClick={() => copyToClipboard(designImage?.orderCode)}>
                  {copied ? '✅ Copied!' : '📋 Copy to Clipboard'}
                </button>
                <a
                  href="https://toastytrainers.com/products/craft-your-card"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cta-btn cta-btn-shopify"
                  onClick={() => track('shopify_listing_clicked', { background: bgObj.label })}
                >
                  <img src="/icons/shopify_glyph.svg" alt="" className="store-btn-icon" />
                  Buy on Shopify
                </a>
                {referralSource !== 'shopify' && (
                  <a
                    href="https://www.etsy.com/ca/listing/4505966199/custom-pokemon-card-fully-customizable"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cta-btn cta-btn-etsy"
                    onClick={() => track('etsy_listing_clicked', { background: bgObj.label })}
                  >
                    <span className="etsy-icon-bg"><img src="/icons/etsy-brands-solid-full.svg" alt="" className="store-btn-icon" /></span>
                    Buy on Etsy
                  </a>
                )}
                <button
                  className="order-guide-toggle"
                  onClick={() => setShowGuide(g => !g)}
                >
                  📍 Where do I paste this on Etsy? {showGuide ? '▲' : '▼'}
                </button>
                {showGuide && (
                  <img
                    src="/personalisationguide.png"
                    alt="Where to paste personalisation on Etsy"
                    className="order-guide-img"
                  />
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PokemonCard;
